import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import DocumentUploader from "@/components/shared/DocumentUploader";
import { Pill, HeartPulse, ScrollText, User, ClipboardList, Save, FolderHeart } from "lucide-react";

const TABS = [
  { key: "medical", label: "Medical profile", icon: <User size={14}/> },
  { key: "medications", label: "Medications", icon: <Pill size={14}/> },
  { key: "allergies", label: "Allergies", icon: <HeartPulse size={14}/> },
  { key: "history", label: "Consultation history", icon: <ScrollText size={14}/> },
  { key: "documents", label: "Documents & tests", icon: <ClipboardList size={14}/> },
];

export default function CarePassport() {
  const [passport, setPassport] = useState(null);
  const [medical, setMedical] = useState({ conditions:"", allergies:"", medications:"", surgeries:"", family_history:"" });
  const [tab, setTab] = useState("medical");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/patient/care-passport").then(r => {
      setPassport(r.data);
      const m = r.data.medical || {};
      setMedical({
        conditions: m.conditions || "", allergies: m.allergies || "",
        medications: m.medications || "", surgeries: m.surgeries || "",
        family_history: m.family_history || "",
      });
    });
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/patient/medical", medical);
      toast.success("Care Passport updated");
    } catch { toast.error("Could not save"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="cm-eyebrow mb-2 flex items-center gap-1.5"><FolderHeart size={12}/> Care Passport</div>
        <h1 className="cm-h2">Your health, in one place.</h1>
      </div>

      <div className="flex flex-wrap gap-2">
        {TABS.map(t => (
          <button key={t.key} onClick={()=>setTab(t.key)}
            className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium border transition ${tab===t.key ? "bg-[#1a2b4a] text-white border-[#1a2b4a]" : "bg-white border-[#e2e8f0] text-[#475569] hover:border-[#4a90d9]"}`}
            data-testid={`passport-tab-${t.key}`}>
            {t.icon}{t.label}
          </button>
        ))}
      </div>

      {tab === "medical" && (
        <div className="cm-card grid sm:grid-cols-2 gap-4">
          <div><label className="cm-label">Existing conditions</label><textarea className="cm-input min-h-[90px]" value={medical.conditions} onChange={e=>setMedical({...medical, conditions:e.target.value})} data-testid="cp-conditions"/></div>
          <div><label className="cm-label">Past surgeries</label><textarea className="cm-input min-h-[90px]" value={medical.surgeries} onChange={e=>setMedical({...medical, surgeries:e.target.value})} data-testid="cp-surgeries"/></div>
          <div><label className="cm-label">Family history</label><textarea className="cm-input min-h-[90px]" value={medical.family_history} onChange={e=>setMedical({...medical, family_history:e.target.value})} data-testid="cp-family"/></div>
          <div><label className="cm-label">Allergies</label><textarea className="cm-input min-h-[90px]" value={medical.allergies} onChange={e=>setMedical({...medical, allergies:e.target.value})} data-testid="cp-allergies"/></div>
          <div className="sm:col-span-2"><label className="cm-label">Medications</label><textarea className="cm-input min-h-[90px]" value={medical.medications} onChange={e=>setMedical({...medical, medications:e.target.value})} data-testid="cp-medications"/></div>
          <div className="sm:col-span-2"><button className="cm-btn-primary" onClick={save} disabled={saving} data-testid="cp-save"><Save size={14}/> {saving?"Saving…":"Save Care Passport"}</button></div>
        </div>
      )}

      {tab === "medications" && (
        <div className="cm-card">
          <div className="text-sm text-[#475569] whitespace-pre-wrap">{medical.medications || "No medications recorded."}</div>
        </div>
      )}
      {tab === "allergies" && (
        <div className="cm-card">
          <div className="text-sm text-[#475569] whitespace-pre-wrap">{medical.allergies || "No allergies recorded."}</div>
        </div>
      )}
      {tab === "history" && (
        <div className="cm-card divide-y divide-[#e2e8f0]" data-testid="cp-history">
          {(passport?.appointments||[]).length === 0 && <div className="text-sm text-[#475569]">No consultations yet.</div>}
          {(passport?.appointments||[]).map(a => {
            const note = (passport.consultation_notes||[]).find(n => n.appointment_id === a.id);
            return (
              <div key={a.id} className="py-4 first:pt-0 last:pb-0">
                <div className="font-semibold text-[#1a2b4a]">{a.doctor_name}</div>
                <div className="text-xs text-[#475569] mb-2 capitalize">{a.specialty?.replace("_"," ")} · {(a.slot_datetime||"").replace("T"," ").slice(0,16)}</div>
                {note?.summary && <div className="text-sm text-[#475569] mt-1"><b>Summary:</b> {note.summary}</div>}
                {note?.advice && <div className="text-sm text-[#475569]"><b>Advice:</b> {note.advice}</div>}
                {note?.medications && <div className="text-sm text-[#475569]"><b>Medications:</b> {note.medications}</div>}
                {note?.follow_ups && <div className="text-sm text-[#475569]"><b>Follow-ups:</b> {note.follow_ups}</div>}
              </div>
            );
          })}
        </div>
      )}
      {tab === "documents" && (
        <DocumentUploader
          kind="patient_document"
          title="Your documents & test results"
          description="Upload test results, scan reports or letters. Your doctor sees them when tied to an appointment."
        />
      )}
    </div>
  );
}
